import { useEffect, useState } from "react";
import { lessonProgressStatus } from "../utils/lessonProgressStatus";
import useAuth from "../hook/useAuth";
import { getCourseProgress } from "../services/Progress";

type CourseProgressProps = {
  courseId: number;
  userId?: number;
}

export const CourseProgress = ({ courseId, userId = 0 }: CourseProgressProps) => {
  const { auth }: any = useAuth();
  const [courseProgress, setCourseProgress] = useState<any[]>([]);
  const [progressDataIsLoaded, setProgressDataIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (courseId && userId > 0) {
      setProgressDataIsLoaded(false);
      getCourseProgress(courseId, userId)
        .then((data) => {
          setCourseProgress(data);
          setProgressDataIsLoaded(true);
        })
        .catch((error) => {
          console.error(error)
        })
    }
    else {
      setProgressDataIsLoaded(false);
      getCourseProgress(courseId, auth?.id)
        .then((data) => {
          setCourseProgress(data);
          setProgressDataIsLoaded(true);
        })
        .catch((error) => {
          console.error(error)
        })
    }

  }, [courseId, auth, userId])

  const getQuizStatus = (value: number, total: number) => {
    if (total === 0 && value === 0)
      return lessonProgressStatus.find((s) => s.label === "Not started");

    const progress = (value / total) * 100;

    if (progress >= 100)
      return lessonProgressStatus.find((s) => s.label === "Proficient");
    if (progress >= 50)
      return lessonProgressStatus.find((s) => s.label === "Familiar");
    if (progress >= 10)
      return lessonProgressStatus.find((s) => s.label === "Attempted");

    return lessonProgressStatus.find((s) => s.label === "Not started");
  };

  const getTooltipMessage = (label: string) => {
    switch (label) {
      case "Mastered":
        return "All quizzes of a sublesson are 100% completed";
      case "Proficient":
        return "A quiz is 100% completed";
      case "Familiar":
        return "A quiz completed 50% or more";
      case "Attempted":
        return "A quiz completed 10% or more";
      case "Not started":
        return "A quiz completion is 0%";
      default:
        return "";
    }
  };




  return (
    <div className="w-full border-t border-gray-300">
      <div className="p-6">
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 border-b border-gray-300 py-4">
          {lessonProgressStatus.map((s, i) => (
            <div key={i} className="relative group flex items-center space-x-2 cursor-pointer">
              {/* Legend Icon */}
              <div
                className={`w-8 h-8 flex justify-center rounded border border-gray-300
          ${s.label !== "Familiar" && s.label !== "Attempted"
                    ? `${s.color} items-center`
                    : "bg-white items-end"
                  }`}
              >
                {s.label !== "Familiar" && s.label !== "Attempted" ? (
                  s.icon || ""
                ) : s.label === "Familiar" ? (
                  <div className={`w-full h-[50%] ${s.color}`} />
                ) : (
                  <div className={`w-full h-[20%] ${s.color}`} />
                )}
              </div>

              <span className="text-sm">{s.label}</span>

              {/* Tooltip */}
              <div className="
        absolute left-1/2 -translate-x-1/2
        bottom-full mb-2
        w-max
        max-w-[200px]
        bg-gray-900 text-white text-xs
        px-2 py-1 rounded
        opacity-0 group-hover:opacity-100
        pointer-events-none
        transition-opacity
        z-50
      ">
                {getTooltipMessage(s.label)}
              </div>
            </div>
          ))}
        </div>

        {progressDataIsLoaded ? (
          <div className="grid grid-cols-2 gap-4">
            {courseProgress.map((lesson, i) => (
              <div
                key={i}
                className="flex gap-1.5 border-b border-[#D0D0D0] py-2 flex-wrap"
              >
                <h4 className="font-medium">{lesson.title}:</h4>

                {lesson.subLessons?.length > 0 ? (
                  <div className="flex flex-wrap items-center">
                    {lesson.subLessons.map((sublesson: any, j: number) => (
                      <div key={j} className="flex items-center">
                        <div className="flex items-center space-x-1">
                          {sublesson.quizzes.length > 0 &&
                            sublesson.quizzes.map((quiz: any, k: number) => {
                              // Check if all quizzes are mastered
                              const allMastered = sublesson.quizzes.every(
                                (q: any) => q.total > 0 && q.value === q.total
                              );

                              // Use Mastered for all if sublesson mastered, else normal logic
                              const status = allMastered
                                ? lessonProgressStatus.find((s) => s.label === "Mastered")
                                : getQuizStatus(quiz.value, quiz.total);

                              return (
                                <div key={k} className="relative group">
                                  <div
                                    className={`w-6 h-6 flex justify-center rounded border border-gray-300 cursor-pointer
                                     ${status?.label !== "Familiar" && status?.label !== "Attempted"
                                        ? `${status?.color} items-center`
                                        : "bg-white items-end"
                                      }`}
                                  >
                                    {status?.label !== "Familiar" && status?.label !== "Attempted" ? (
                                      status?.icon || ""
                                    ) : status?.label === "Familiar" ? (
                                      <div className={`w-full h-[50%] ${status?.color}`} />
                                    ) : (
                                      <div className={`w-full h-[20%] ${status?.color}`} />
                                    )}
                                  </div>

                                  {/* Tooltip */}
                                  <div
                                    className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                                  bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 
                                    transition-opacity duration-200 pointer-events-none z-10"
                                  >
                                    {quiz.title}
                                  </div>
                                </div>

                              );
                            })}
                        </div>

                        {/* Divider between sublessons */}
                        {(lesson.subLessons.length > 1 && j < (lesson.subLessons.length - 1) && sublesson.quizzes.length > 1) && (
                          <span className="text-gray-400 inline-block mx-1">|</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-400 ml-2 text-sm italic">
                    No sub-lessons
                  </span>
                )}

              </div>
            ))}
          </div>
        ) : (

          <div className="flex items-center justify-center w-full p-2">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
          </div>
        )}
      </div>
    </div>

  )
}